/**
 * API Client Service
 * 
 * Browser-compatible service for communicating with the Customer API from the frontend
 */

import type { 
    ApiQuoteRequest, 
    ApiQuoteFieldValue, 
    ApiFieldDefinition,
    ApiUser,
    QuoteStatus
} from '../models/apiTypes';

interface ApiResponse<T = any> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
}

export class ApiClientService {
    private baseUrl: string;
    private apiKey: string | null = null;

    constructor(baseUrl = '/api/v1') {
        this.baseUrl = baseUrl;
    }

    /**
     * Set API key for authentication
     */
    setApiKey(apiKey: string) {
        this.apiKey = apiKey;
    }

    /**
     * Make authenticated API request
     */
    private async makeRequest<T>(
        endpoint: string, 
        options: RequestInit = {}
    ): Promise<ApiResponse<T>> {
        try {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                ...((options.headers as Record<string, string>) || {})
            };

            if (this.apiKey) {
                headers['X-API-Key'] = this.apiKey;
            }

            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...options,
                headers
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    error: data.error || 'Request failed',
                    message: data.message
                };
            }

            return {
                success: true,
                data
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Network error'
            };
        }
    }

    /**
     * Make unauthenticated API request (no API key header)
     */
    private async makeUnauthenticatedRequest<T>(
        endpoint: string, 
        options: RequestInit = {}
    ): Promise<ApiResponse<T>> {
        try {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                ...((options.headers as Record<string, string>) || {})
            };

            // Explicitly do NOT add the X-API-Key header

            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...options,
                headers
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    error: data.error || 'Request failed',
                    message: data.message
                };
            }

            return {
                success: true,
                data,
                message: data.message
            };

        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Network error'
            };
        }
    }

    /**
     * Health check
     */
    async healthCheck(): Promise<ApiResponse> {
        return this.makeRequest('/health');
    }

    /**
     * Submit quote request
     */
    async submitQuote(quoteData: any): Promise<ApiResponse> {
        return this.makeRequest('/submit-quote', {
            method: 'POST',
            body: JSON.stringify(quoteData)
        });
    }

    /**
     * Retrieve quote price
     */
    async retrievePrice(productNumber: string): Promise<ApiResponse> {
        return this.makeRequest(`/retrieve-price/${encodeURIComponent(productNumber)}`);
    }

    /**
     * Confirm order
     */
    async confirmOrder(productNumber: string): Promise<ApiResponse> {
        return this.makeRequest('/confirm-order', {
            method: 'POST',
            body: JSON.stringify({ ProductNumber: productNumber })
        });
    }

    /**
     * Check status
     */
    async checkStatus(productNumber: string): Promise<ApiResponse> {
        return this.makeRequest(`/check-status/${encodeURIComponent(productNumber)}`);
    }

    /**
     * Create session API key for user
     */
    async createSession(userData: {name: string; email: string; company?: string}): Promise<ApiResponse<{apiKey: string; userId: string; email: string; name: string}>> {
        // Use direct server base URL for public endpoints, not /api/v1
        const serverBaseUrl = this.baseUrl.replace('/api/v1', '');
        
        const response = await fetch(`${serverBaseUrl}/public/create-session`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(userData)
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                error: data.error || 'Request failed',
                message: data.message
            };
        }

        return {
            success: true,
            data,
            message: data.message
        };
    }
}

// Mock implementations for browser compatibility
export class MockApiQuoteRepository {
    private async makeAuthenticatedRequest<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        try {
            // Get Firebase auth token for admin endpoints
            const { authService } = await import('./authService');
            const token = await authService.getIdToken();
            
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(`/api/v1/admin${endpoint}`, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    ...(options.headers || {})
                }
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.statusText}`);
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || 'API request failed');
            }

            return data.data || data;
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    async findAll(status?: QuoteStatus): Promise<ApiQuoteRequest[]> {
        try {
            const endpoint = status ? `/quotes?status=${encodeURIComponent(status)}` : '/quotes';
            return await this.makeAuthenticatedRequest<ApiQuoteRequest[]>(endpoint);
        } catch (error) {
            console.error('Failed to fetch quotes:', error);
            return []; // Return empty array on failure for now
        }
    }

    async getQuoteStats(): Promise<{
        totalQuotes: number;
        quotesNeedingApproval: number;
        duplicateQuotes: number;
        quotesByStatus: Record<QuoteStatus, number>;
    }> {
        try {
            return await this.makeAuthenticatedRequest<{
                totalQuotes: number;
                quotesNeedingApproval: number;
                duplicateQuotes: number;
                quotesByStatus: Record<QuoteStatus, number>;
            }>('/quotes/stats');
        } catch (error) {
            console.error('Failed to fetch quote stats:', error);
            return {
                totalQuotes: 0,
                quotesNeedingApproval: 0,
                duplicateQuotes: 0,
                quotesByStatus: {} as Record<QuoteStatus, number>
            };
        }
    }

    async update(id: string, updates: Partial<ApiQuoteRequest>): Promise<ApiQuoteRequest> {
        return await this.makeAuthenticatedRequest<ApiQuoteRequest>(`/quotes/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
        });
    }

    async softDelete(id: string): Promise<ApiQuoteRequest> {
        return await this.makeAuthenticatedRequest<ApiQuoteRequest>(`/quotes/${id}/delete`, {
            method: 'POST'
        });
    }

    async changeStatus(id: string, newStatus: QuoteStatus): Promise<ApiQuoteRequest> {
        return await this.makeAuthenticatedRequest<ApiQuoteRequest>(`/quotes/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status: newStatus })
        });
    }
}

export class MockApiFieldRepository {
    async findFieldValues(quoteId: string): Promise<ApiQuoteFieldValue[]> {
        return [];
    }

    async findAllDefinitions(publishedOnly?: boolean): Promise<ApiFieldDefinition[]> {
        return [];
    }

    async updateFieldValue(id: string, updates: Partial<ApiQuoteFieldValue>): Promise<ApiQuoteFieldValue> {
        throw new Error('Not implemented - would make API call to server');
    }

    async createDefinition(definition: any): Promise<ApiFieldDefinition> {
        throw new Error('Not implemented - would make API call to server');
    }

    async getFieldStats(): Promise<{
        totalDefinitions: number;
        publishedDefinitions: number;
        unapprovedDefinitions: number;
        totalFieldValues: number;
        fieldValuesNeedingApproval: number;
        unknownFields: number;
    }> {
        return {
            totalDefinitions: 0,
            publishedDefinitions: 0,
            unapprovedDefinitions: 0,
            totalFieldValues: 0,
            fieldValuesNeedingApproval: 0,
            unknownFields: 0
        };
    }
}

export class MockApiUserRepository {
    async getUserStats(): Promise<{
        totalUsers: number;
        activeUsers: number;
        usersByType: Record<string, number>;
        recentlyActiveUsers: number;
    }> {
        return {
            totalUsers: 0,
            activeUsers: 0,
            usersByType: {},
            recentlyActiveUsers: 0
        };
    }
}

export class MockApiPricingIntegrationService {
    async processQuotePricing(quoteId: string): Promise<any> {
        throw new Error('Not implemented - would make API call to server');
    }
}

// Export singleton instances
export const apiClientService = new ApiClientService();
export const mockApiQuoteRepository = new MockApiQuoteRepository();
export const mockApiFieldRepository = new MockApiFieldRepository();
export const mockApiUserRepository = new MockApiUserRepository();
export const mockApiPricingIntegrationService = new MockApiPricingIntegrationService();